import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { ADD_VIOLATION_DRAWER_QUERY_KEY } from 'Deer/components/DisciplinaryAction/BottomDrawers/CreateViolationDrawer/constants';
import { CancelConfirmationModal } from 'Deer/pages/Company/DisciplinaryActions/DisciplinaryActionsSettings/CancelConfirmationModal';
import { useBulkActionContext } from 'Deer/pages/Company/DisciplinaryActions/components/BulkActionHeader/BulkActionContext';
import { useNotifyPeople } from 'Deer/pages/Company/DisciplinaryActions/dataHooks';
import { ReportedEmployee } from 'Deer/pages/MyData/DisciplinaryActions/components/ReportedEmployee';
import {
  useGetDisciplinaryActions,
  usePublishDisciplinaryNote,
  useInvalidateGetDisciplinaryActions,
  useBulkPublishDisciplinaryNotes,
  useDeleteViolationNote,
} from 'Deer/pages/MyData/DisciplinaryActions/dataHooks';
import type { GridApi, RowClickedEvent } from 'ag-grid-community';
import { useToast } from 'components/hooks/useToast';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import NoDisciplinaryActions from 'assets/assetsV1/illustrations/noDisciplinaryActions.svg';

import { isMobileView } from 'constants/isMobileView';
import { DISCIPLINARY_ACTIONS_INTENT_ID_MAP } from 'constants/moduleIntentTypes';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridKebabMenuOptions } from 'uiToolkitV1/AgGridWrapperV2/types';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';

import { BulkPublishAndAddViolationHeader } from './components/AddViolationAndBulkPublishHeader';
import { DISCIPLINARY_ACTIONS_COLUMN_ORDER, DISCIPLINARY_ACTIONS_STATUS } from './constants';

// Proper TypeScript interfaces
interface MyTeamsDisciplinaryActionsGridProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridRef?: React.RefObject<any>;
  gridStatePersistenceKey: string;
  intentId?: string;
}

interface ColumnProps {
  columnId: string;
  displayName: string;
  description?: string;
  [key: string]: unknown;
}

interface ColumnDef {
  colId: string;
  field: string;
  id: string;
  displayName: string;
  description?: string;
  minWidth: number;
  [key: string]: unknown;
}

function MyTeamsDisciplinaryActionsGrid({
  gridRef,
  gridStatePersistenceKey,
  intentId,
}: MyTeamsDisciplinaryActionsGridProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentCompRef = useRef<any>(null);
  const agGridRef = gridRef ?? currentCompRef;
  const cohortDetails = useSelectedView();
  const history = useHistory();
  const { search } = useLocation();
  const toast = useToast();
  const [columnDefs, setColumnDefs] = useState<ColumnDef[]>([]);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState(null);

  const {
    isFetching,
    data: disciplinaryActionsData,
    error,
  } = useGetDisciplinaryActions({
    cohortId: cohortDetails?.id,
    status: intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.ALL ? undefined : 'pending',
  });

  const { columns, rows = [] } = disciplinaryActionsData ?? {};
  const { mutate: publishNote } = usePublishDisciplinaryNote();
  const { mutate: bulkPublishNotes } = useBulkPublishDisciplinaryNotes();
  const { mutate: deleteNote } = useDeleteViolationNote();
  const { mutate: notifyPeople } = useNotifyPeople();

  const loggedInUserId = useSelector(selectLoggedInEmployeeId);

  const addViolationHandler = () => {
    const queryParams = new URLSearchParams(search);
    queryParams.set(ADD_VIOLATION_DRAWER_QUERY_KEY, 'true');
    history.push({
      search: queryParams.toString(),
    });
  };

  const onRowClicked = useCallback(
    (row: RowClickedEvent) => {
      const { data } = row;
      const queryParams = new URLSearchParams();
      queryParams.set('viewViolation', 'true');
      queryParams.set('violationNoteId', data.NOTE_ID);
      history.push({ search: queryParams.toString() });
    },
    [history]
  );

  const {
    setTotalDisciplinaryActions,
    setSelectedDisciplinaryActionIds,
    resetSelection,
    selectedDisciplinaryActionIds,
  } = useBulkActionContext();

  const mobileViewProps = useMemo(
    () =>
      isMobileView()
        ? {
            cellRenderer: ReportedEmployee,
            cellRendererParams: {
              screenType: 'MANAGER',
            },
            autoHeight: true,
            cellStyle: { whiteSpace: 'normal', padding: '4px', width: 'calc(100% - 30px)' },
            headerStyle: { padding: '4px' },
            headerComponent: CustomHeaderCell,
            headerComponentParams: {
              enableMenu: true,
              enableSorting: false,
              height: '22px',
            },
          }
        : {},
    []
  );

  // Define column-specific properties map
  const customColumnPropsMap = useMemo(
    () => ({
      RAISED_FOR: {
        flex: 1,
        autoHeight: true,
        metadata: { isEmployeeSubtitle: true },
        ...mobileViewProps,
      },
      RAISED_BY: {
        metadata: { isEmployeeSubtitle: true },
      },
      DESCRIPTION: {
        minWidth: 400,
      },
    }),
    [mobileViewProps]
  );

  useEffect(() => {
    if (!columns || columns.length === 0) return;

    const newColumnDefs = columns
      .map((col: ColumnProps) => ({
        colId: col.columnId,
        field: col.columnId,
        id: col.columnId,
        displayName: col.displayName,
        description: col.description,
        headerName: col.displayName,
        minWidth: 220,

        ...getColumnDefinitionByType(col),
        ...(customColumnPropsMap[col.columnId] || {}),
      }))
      .filter((col) => {
        if (isMobileView()) return col.field === 'RAISED_FOR';
        if (col.field === 'NOTE_ID') return false;

        return true;
      })
      .sort(
        (a, b) =>
          DISCIPLINARY_ACTIONS_COLUMN_ORDER.findIndex((c) => c.colId === a.colId) -
          DISCIPLINARY_ACTIONS_COLUMN_ORDER.findIndex((c) => c.colId === b.colId)
      );

    setColumnDefs(newColumnDefs);
  }, [columns, mobileViewProps, customColumnPropsMap]);

  const onSelectionChanged = useCallback(
    (event) => {
      const api = event.api as GridApi;
      setSelectedDisciplinaryActionIds(api.getSelectedRows().map((e) => +(e?.NOTE_ID || '')));
    },
    [setSelectedDisciplinaryActionIds]
  );

  const handleFilterAndDataChange = useCallback(() => {
    let total = 0;
    const api = agGridRef.current?.api as GridApi | undefined;

    if (api?.isAnyFilterPresent()) {
      api.forEachNodeAfterFilter(() => {
        total += 1;
      });
    } else {
      total = rows?.length || 0;
    }
    setTotalDisciplinaryActions(total);
  }, [agGridRef, rows?.length, setTotalDisciplinaryActions]);

  // Handle row data updates
  const onRowDataUpdated = useCallback(() => {
    resetSelection();
    handleFilterAndDataChange();
  }, [handleFilterAndDataChange, resetSelection]);

  // Get the invalidate function
  const invalidateDisciplinaryActions = useInvalidateGetDisciplinaryActions();

  // Handle publishing a disciplinary action
  const handlePublish = useCallback(
    (noteId) => {
      if (!noteId) {
        console.error('No noteId provided for publishing disciplinary action');
        return;
      }

      // Call the mutation directly with the noteId
      publishNote(
        { noteId: parseInt(noteId, 10) },
        {
          onSuccess: () => {
            invalidateDisciplinaryActions();
          },
          onError: (err) => {
            console.error('Error publishing disciplinary note', err);
          },
        }
      );
    },
    [publishNote, invalidateDisciplinaryActions]
  );

  const handleNotify = useCallback(
    (noteId: number) => {
      notifyPeople([noteId], {
        onSuccess: () => {
          toast({
            title: 'Notified successfully',
            status: 'success',
          });
        },
        onError: (err) => {
          console.error('Error notifying people', err);
          toast({
            title: 'Error notifying people',
            status: 'error',
          });
        },
      });
    },
    [notifyPeople, toast]
  );

  const handleDelete = useCallback(
    (noteId) => {
      if (!noteId) {
        console.error('No noteId provided for publishing disciplinary action');
        return;
      }

      deleteNote(
        { noteId: +noteId },
        {
          onSuccess: () => {
            invalidateDisciplinaryActions();
            toast({
              title: 'Disciplinary note deleted successfully',
              status: 'success',
            });
            setDeleteNoteId(null);
            setIsConfirmDeleteModalOpen(false);
          },
          onError: (err) => {
            console.error('Error deleting disciplinary note', err);
            toast({
              title: 'Error deleting disciplinary note',
              status: 'error',
            });
            setDeleteNoteId(null);
            setIsConfirmDeleteModalOpen(false);
          },
        }
      );
    },
    [deleteNote, invalidateDisciplinaryActions, toast]
  );

  // Memoize kebab menu options generator
  const kebabMenuOptionsGenerator = useCallback(
    (data) => {
      const options: AgGridKebabMenuOptions = [];
      const { NOTE_ID, RAISED_BY } = data;
      // TODO: Check if the logged in user is manager of the reported employee and allow publish , BE is not sending info about manager yet.
      if (data?.STATUS === DISCIPLINARY_ACTIONS_STATUS.APPROVED && RAISED_BY?.id === +loggedInUserId) {
        options.push({
          label: 'Publish',
          onClick: () => handlePublish(NOTE_ID),
        });
      } else if (data?.STATUS === DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE) {
        options.push({
          label: 'Notify employee',
          onClick: () => handleNotify(NOTE_ID),
        });
      }
      return options;
    },
    [loggedInUserId, handlePublish, handleNotify]
  );

  // Handle bulk publish action
  const handleBulkPublish = useCallback(() => {
    if (selectedDisciplinaryActionIds.length === 0) {
      console.error('No notes selected for bulk publish');
      return;
    }
    bulkPublishNotes(
      { noteIds: selectedDisciplinaryActionIds },
      {
        onSuccess: () => {
          invalidateDisciplinaryActions();
          resetSelection();
          toast({
            title: 'Disciplinary notes published successfully',
            status: 'success',
          });
        },
        onError: (err) => {
          console.error('Error bulk publishing disciplinary notes', err);
          toast({
            title: 'Error bulk publishing disciplinary notes',
            status: 'error',
          });
        },
      }
    );
  }, [selectedDisciplinaryActionIds, bulkPublishNotes, invalidateDisciplinaryActions, resetSelection, toast]);

  // Memoize sidebar configuration
  const sidebarConfig = useMemo(
    () =>
      isMobileView()
        ? false
        : {
            position: 'right' as const,
            toolPanels: ['columns'],
            defaultToolPanel: undefined,
          },
    []
  );

  // Memoize additionalMobileProps configuration
  const additionalMobileProps = useMemo(
    () => ({
      pinned: undefined,
      lockPosition: undefined,
    }),
    []
  );

  // Error state handling
  if (error) {
    return (
      <EmptyScreen
        heading="Actions not available!"
        description="There was an issue fetching the actions. Please try again later."
        illustrationSrc={NoDisciplinaryActions}
      />
    );
  }

  return (
    <Box
      display="flex"
      padding="16px 12px 8px 12px"
      flexDirection="column"
      alignItems="flex-start"
      gap="var(--size-300, 12px)"
      borderRadius="8px"
      background="var(--White, #FFF)"
      boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)"
    >
      <BulkPublishAndAddViolationHeader
        isBulkPublishDisabled={selectedDisciplinaryActionIds.length === 0}
        onBulkPublish={handleBulkPublish}
        onAddViolation={addViolationHandler}
        isBulkPublishVisible={intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.PENDING}
      />
      {rows?.length === 0 && !isFetching ? (
        <Box height="70vh" display="flex" justifyContent="center" alignItems="center" width="100%">
          <EmptyScreen
            heading={
              intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.PENDING
                ? 'No pending disciplinary actions'
                : 'No disciplinary actions'
            }
            illustrationSrc={NoDisciplinaryActions}
          />
        </Box>
      ) : (
        <Box
          className="disciplinary-actions-grid all-disciplinary-actions-grid"
          width="100%"
          height="calc(100vh - 265px)"
        >
          <AgGridTable
            key={gridStatePersistenceKey}
            gridStatePersistenceKey={gridStatePersistenceKey}
            ref={agGridRef}
            rowData={rows}
            columnDefs={columnDefs}
            domLayout="normal"
            pagination
            paginationPageSize={10}
            paginationAutoPageSize={!!isMobileView()}
            headerHeight={isMobileView() ? 20 : 40}
            rowHeight={isMobileView() ? 167 : 48}
            rowStyle={{ cursor: 'default' }}
            onSelectionChanged={onSelectionChanged}
            onRowDataUpdated={onRowDataUpdated}
            kebabMenuOptionsGenerator={kebabMenuOptionsGenerator}
            kebabMenuIsRowSpecific={!isMobileView()}
            height="100%"
            sideBar={sidebarConfig}
            showSidebarToggle={false}
            /* In Mobile view, we have only one column, so pinning it left is creating empty space in center */
            additionalMobileProps={additionalMobileProps}
            rowSelection={
              intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.PENDING
                ? {
                    mode: 'multiRow',
                    enableClickSelection: false,
                    headerCheckbox: true,
                  }
                : undefined
            }
            loading={isFetching}
            onRowClicked={onRowClicked}
            defaultColDef={{
              resizable: false,
            }}
          />
          {isConfirmDeleteModalOpen && (
            <CancelConfirmationModal
              isOpen={isConfirmDeleteModalOpen}
              onClose={() => setIsConfirmDeleteModalOpen(false)}
              onContinue={() => handleDelete(deleteNoteId)}
              modalAgenda="delete"
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export { MyTeamsDisciplinaryActionsGrid };
